import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Nav
    home: 'Home',
    about: 'About',
    services: 'Services',
    ecosystem: 'Ecosystem',
    contact: 'Contact',

    // Hero
    heroTagline: 'Web design, ads, and digital growth under one roof',
    heroTitle: 'We Build Websites, Run Ads & Scale Your Business',
    heroSubtitle: 'CEXUS is the team behind high-performing websites, targeted ad campaigns, and full-scale digital ecosystems. We handle everything from design to launch to growth so you can focus on running your business.',
    heroCtaPrimary: 'Get Started',
    heroCtaSecondary: 'Explore Ecosystem',

    // Stats
    stat1Number: '6+',
    stat1Label: 'Platforms Built & Powered',
    stat2Number: '100%',
    stat2Label: 'Hands-On, Dedicated Support',

    // Why Us
    whyUsTag: 'Why CEXUS',
    whyUsTitle: 'Because Your Business Deserves More Than a Template',
    whyUsSubtitle: 'We have been where you are. Hustling to get your brand seen, figuring out the tech, trying to scale without burning out. That is exactly why CEXUS exists.',
    whyUs1Title: 'We Don\'t Just Build Websites. We Build Businesses',
    whyUs1Desc: 'Anyone can put up a page. We design websites that actually bring in clients, build trust, and grow with you. Your website is your storefront, your pitch deck, and your reputation all in one. We treat it that way.',
    whyUs2Title: 'Ads That Actually Convert',
    whyUs2Desc: 'We don\'t just run ads. We build targeted campaigns on Google, Meta, and social platforms that bring real customers to your door. Data-driven strategy, not guesswork.',
    whyUs3Title: 'Built by People Who Actually Get It',
    whyUs3Desc: 'We are not a faceless agency. We are a small, sharp team that understands what it takes to grow from the ground up. Every platform in our ecosystem was built with that same energy. Real solutions for real people.',
    whyUs4Title: 'Scale Without Limits',
    whyUs4Desc: 'Whether you are launching your first site or expanding into new markets, CEXUS infrastructure is built to grow with you. No ceilings, no bottlenecks. Just clean, reliable systems that keep up with your ambition.',

    // About
    aboutTag: 'About the CEXUS Platform',
    aboutTitle: 'A Unified, Resilient Digital Network',
    aboutVision: 'The CEXUS ecosystem envisions a world where individuals and communities evolve through meaningful contribution, strategic alignment, and shared infrastructure. CEXUS is not just a platform. It is the trusted digital network connecting ideas to execution across the entire ecosystem.',
    whoWeAreTag: 'What is CEXUS',
    whoWeAreTitle: 'Independent Infrastructure, Limitless Potential',
    whoWeAreText: 'CEXUS is a secure digital platform and independent infrastructure system designed to power platforms and communities at scale. By connecting people, skills, and ideas into a cohesive, verified network, the CEXUS system provides the operational backbone that enables independent ecosystems to function, grow, and deliver impact.',
    missionTag: 'The CEXUS Mission',
    missionTitle: 'Enabling Safe, Scalable Digital Collaboration',
    missionText: 'Our mission is to remove the barriers that limit how businesses and communities grow online. The CEXUS platform provides the frameworks, tools, and infrastructure that allow ecosystems to operate independently while remaining interconnected through the trusted CEXUS network.',

    // Services (homepage preview)
    servicesTag: 'What We Do',
    servicesTitle: 'Web Design, Advertising & Digital Growth',
    servicesSubtitle: 'From custom websites to ad campaigns to full business ecosystems, we have got you covered.',
    service1Title: 'Web Design & Development',
    service1Desc: 'Custom-built websites that look sharp, load fast, and convert visitors into clients. No templates, no shortcuts. Just clean, professional design tailored to your brand.',
    service2Title: 'Google & Social Media Ads',
    service2Desc: 'Targeted advertising campaigns on Google, Facebook, Instagram, and more. We create, manage, and optimize your ads to bring real customers and measurable results.',
    service3Title: 'Business Infrastructure & Growth',
    service3Desc: 'The systems and strategy behind the scenes that keep your business running smoothly. From hosting and SEO to analytics and scaling.',

    // Projects / Ecosystem
    projectsTag: 'Our Ecosystem',
    projectsTitle: 'Platforms Powered by CEXUS',
    projectsSubtitle: 'Each platform operates independently while leveraging shared CEXUS infrastructure for maximum performance and reach.',

    bituPros: 'BituPros Solutions',
    bituProsDesc: 'A full-service solutions platform helping businesses establish and strengthen their digital presence with professional web services, strategy, and infrastructure powered by CEXUS.',
    streetzIndeed: 'Streetz Indeed (SI)',
    streetzDesc: 'A trusted digital platform built to amplify authentic voices and stories, creating real opportunities through content and community. Powered by CEXUS infrastructure.',
    designerMarket: 'Designer Market',
    designerMarketDesc: 'A creative marketplace connecting designers and clients, offering premium design services and custom solutions for businesses looking to stand out in their market.',
    alturaLiving: 'Altura Living',
    alturaLivingDesc: 'A premium lifestyle and living platform elevating how people discover and experience curated spaces, services, and modern living solutions.',
    ovigo: 'Ovigo',
    ovigoDesc: 'A social co-ridesharing platform connecting drivers and passengers for shared rides across communities. Making travel more affordable, social, and sustainable.',
    enterPlatform: 'Visit Platform',
    explorePlatform: 'Visit Platform',

    comingSoonTag: 'Coming Soon',
    comingSoonTitle: 'Next in the Ecosystem',
    mtlNetwork: 'MTL Music Network',
    mtlDesc: 'A comprehensive music industry platform connecting artists, producers, and industry professionals across the Montreal ecosystem. Currently in development.',
    comingSoonLabel: 'Coming Soon',

    // Contact
    contactTag: 'Get In Touch',
    contactTitle: 'Let\'s Build Something Together',
    contactSubtitle: 'Ready to take your business to the next level? Fill out the form below and we will get back to you.',
    contactNameLabel: 'Full Name',
    contactEmailLabel: 'Email Address',
    contactPhoneLabel: 'Phone Number',
    contactBusinessLabel: 'Business Name',
    contactWebsiteLabel: 'Your Website (if any)',
    contactLinkedinLabel: 'Google Profile or LinkedIn URL',
    contactMessageLabel: 'Tell us about your project',
    contactSubmit: 'Send Message',
    contactInfoTitle: 'Reach Us Directly',
    contactInfoText: 'Prefer to reach out directly? Email us anytime. We are real people and we respond fast.',

    // Footer
    footerTagline: 'Web design, advertising, and digital infrastructure for growing businesses. The CEXUS platform powers trusted community networks and business growth.',
    footerRights: 'All rights reserved.',
    footerFoundedBy: 'Founded by',
    footerEcosystem: 'Ecosystem',
    contact: 'Contact',
    beyondReality: 'Beyond Reality',

    // Website Services page
    viewPricing: 'View Services & Pricing',
    wsTag: 'Website Services',
    wsTitle: 'Websites That Grow Your Business.',
    wsSubtitle: 'We design fast, modern, conversion-focused websites that help businesses attract more customers, build trust, and increase revenue.',
    wsCtaPrimary: 'Build My Website',
    wsCtaSecondary: 'View Packages',
    wsPricingTag: 'Pricing',
    wsPricingTitle: 'Choose Your Growth Package',
    wsPricingSubtitle: 'Transparent pricing. Premium quality. Every package is built to convert visitors into customers.',
    wsMostPopular: 'Most Popular',
    wsStartingAt: 'Starting at',
    wsBuilderTag: 'Interactive Builder',
    wsBuilderTitle: 'Customize Your Website',
    wsBuilderSubtitle: 'Select a package, add the features your business needs, and watch your investment update in real time.',
    wsStepPackage: '1. Select your package',
    wsStepAddons: '2. Choose your add-ons',
    wsYourBuild: 'Your Build',
    wsBasePackage: 'Base package',
    wsNoAddons: 'No add-ons selected yet',
    wsOneTimeTotal: 'One-time total',
    wsMonthlyServices: 'Monthly services',
    wsPerMonth: '/month',
    wsSendQuote: 'Send My Quote',
    wsOrContactPage: 'Or continue on the contact page',
    wsQuoteFormTitle: 'Where should we send your quote?',
    wsQuoteName: 'Full Name',
    wsQuoteEmail: 'Email Address',
    wsQuotePhone: 'Phone Number',
    wsQuoteSubmit: 'Send Quote Request',
    wsQuoteSending: 'Sending...',
    wsQuoteSuccess: 'Quote request sent! We will get back to you within 24 hours.',
    wsQuoteError: 'Something went wrong. Please try again or use the contact page.',
    wsCompareTag: 'Compare',
    wsCompareTitle: 'Every Feature, Side by Side',
    wsCompareSubtitle: 'See exactly what is included in each package.',
    wsCompareFeature: 'Feature',
    wsFaqTag: 'FAQ',
    wsFaqTitle: 'Frequently Asked Questions',
    wsFinalTitle: 'Let\'s Build Something Exceptional.',
    wsFinalText: 'Whether you\'re launching a new business or upgrading your online presence, Cexus delivers websites built to convert visitors into customers.',
    wsFinalCta: 'Start My Project',

    // Game menu / cinematic services
    gmMenuPackages: 'View Packages',
    gmMenuCustomize: 'Customize Your Website',
    gmMenuCompare: 'Compare Features',
    gmMenuStart: 'Start My Project',
    gmSelectHint: 'Select an option to begin',
    carouselTag: 'Choose Your Package',
    carouselTitle: 'Four Paths. One Destination: Growth.',
    carouselSelect: 'Select & Customize',
    carouselContact: 'Request via contact',
    techTag: 'Our Arsenal',
    techTitle: 'Technologies & Platforms We Work With',
    czTeaserTag: 'Interactive Experience',
    czTeaserText: 'Step into our interactive studio. Pick your package, power it up with add-ons, set your ads budget, and secure your build, all in one immersive experience.',
    czStart: 'START',
    czSkip: 'Skip Intro',
    czIntroLine1: 'INITIALIZING BUILD SEQUENCE',
    czIntroLine2: 'Preparing your experience...',
    czChoosePackage: 'Select Your Package',
    czChooseSub: 'Every build starts with a foundation. Choose yours.',
    czBuildTitle: 'Power Up Your Build',
    czAddonsLabel: 'Add-ons',
    czAdsTitle: 'Ads Management',
    czAdsSub: 'Let us run your campaigns. Adjust your monthly budget. We suggest a starting point based on your package.',
    czSuggested: 'Suggested for',
    czMonthlyBudget: 'Monthly budget',
    czDeposit: '35% deposit due today',
    czRemaining: 'Balance on delivery',
    czSecurePay: 'Pay Deposit and Secure My Project',
    czYourInfo: 'Your details to complete checkout',
    czProceedPay: 'Proceed to Secure Payment',
    czRedirecting: 'Redirecting to secure payment...',
    czChangePackage: 'Change package',
    czPayNote: 'Secured by Stripe. Monthly services renew automatically and can be cancelled anytime from your dashboard. The remaining balance is only due when your project is delivered.',
    czPayError: 'Could not start checkout. Please try again or contact us.',
    paySuccessTitle: 'Deposit Received. Let\'s Build.',
    paySuccessText: 'Your project is officially locked in. A confirmation email with your full build summary is on its way. Our team will reach out within 24 hours to kick things off.',
    payChecking: 'Verifying your payment...',
    payFailedTitle: 'Payment Not Completed',
    payFailedText: 'Your payment session expired or was cancelled. No charge was made.',
    payBackServices: 'Back to Services',
    payTryAgain: 'Try Again',
    faqPageTag: 'Support',
    footerLegal: 'Legal',
    footerFaq: 'FAQ',
    footerPrivacy: 'Privacy Policy',
    footerTerms: 'Terms & Conditions',

    // AI Website Audit
    gmMenuAudit: 'Free Website Health Check',
    auditTag: 'Free Website Check',
    auditTitle: 'How Healthy Is Your Website?',
    auditSubtitle: 'Drop your link and get a professional review of your website in seconds. We score it out of 100 and show exactly what is costing you customers. Free, no strings attached.',
    auditPlaceholder: 'yourwebsite.com',
    auditButton: 'Run Free Audit',
    auditStep1: 'Crawling your pages...',
    auditStep2: 'Analyzing design and UX...',
    auditStep3: 'Checking SEO signals...',
    auditStep4: 'Scoring conversion potential...',
    auditWait: 'This usually takes about 30 seconds',
    auditErrorReach: 'We could not reach that website. Check the address and try again.',
    auditErrorGeneric: 'Analysis failed. Please try again in a moment.',
    auditScoreLabel: 'Overall Score',
    auditPagesScanned: 'pages scanned',
    auditBreakdown: 'The Breakdown',
    auditTopFixes: 'Top 5 Fixes',
    auditRecoTitle: 'What We Recommend',
    auditRecoSub: 'Based on what we found, these Cexus services would move the needle the most.',
    auditRecoPackage: 'Best-fit package',
    auditCtaBuild: 'Fix It With Cexus',
    auditCtaTalk: 'Talk To Us First',
    auditAnother: 'Audit another site',

    // Contact wizard
    cwStep1Title: 'About You',
    cwStep2Title: 'Your Business',
    cwStep3Title: 'Your Project',
    cwNext: 'Next',
    cwBack: 'Back',
    contactGoogleLabel: 'Google Business Page',
    cwSendInquiry: 'Send Inquiry',
    cwSending: 'Sending...',
    cwOptionalHint: 'Optional, but it helps us prepare for your project.',
    cwSuccessTitle: 'Inquiry Sent',
    cwSuccessText: 'Thanks for reaching out. We will get back to you within 24 hours.',
    cwSendAnother: 'Send another inquiry',
    cwFillRequired: 'Please fill in all required fields.',
    cwInvalidEmail: 'Please enter a valid email address.',
    contactPhoneTitle: 'Phone',

    // Auth + dashboard
    navSignIn: 'Sign In',
    navDashboard: 'Dashboard',
    navSignOut: 'Sign Out',
    dbTag: 'Client Space',
    dbWelcome: 'Welcome back',
    dbSignInTitle: 'Your Client Space',
    dbSignInText: 'Sign in or create an account to view your purchases, manage your subscriptions, and track your projects.',
    dbSignInBtn: 'Sign In or Create Account',
    dbOrdersTitle: 'Your Purchases',
    dbNoOrders: 'No purchases yet. Your projects and services will appear here.',
    dbBrowse: 'Explore Services',
    dbSubsTitle: 'Your Subscriptions',
    dbNoSubs: 'No active subscriptions.',
    dbCancelSub: 'Cancel Subscription',
    dbCancelConfirm: 'Cancel at the end of the current billing period?',
    dbCancelKeep: 'Keep it',
    dbCancelYes: 'Yes, cancel',
    dbCancelScheduled: 'Cancels at period end',
    dbActive: 'Active',
    dbRenews: 'Renews',
    dbDeposit: 'Deposit paid',
    dbBalance: 'Balance on delivery',
    dbMonthlyLabel: 'Monthly',
    dbOrderDate: 'Purchased',
    dbStatusPaid: 'Paid',

    // Admin
    adTag: 'Admin Control',
    adTitle: 'Business Overview',
    adRevenue: 'Deposits Collected',
    adMrr: 'Monthly Recurring',
    adUsers: 'Users',
    adOrders: 'Paid Orders',
    adSubs: 'Active Subscriptions',
    adLeads: 'Audit Leads',
    adActivity: 'Recent Activity',
    adCustomers: 'Customers',
    adSearchPh: 'Search name or email...',
    adAllServices: 'All services',
    adMinSpent: 'Min spent (CA$)',
    adColOrders: 'Orders',
    adColSpent: 'Total spent',
    adNoCustomers: 'No customers match these filters.',
    adBack: 'Back to customers',
    adRegistered: 'Google account',
    adGuest: 'Guest checkout',
    adPurchases: 'Purchase History',
    adLeadHistory: 'Audit Leads',
    adNoOrders: 'No purchases on record.',

    // Audit email gate
    auditGateTitle: 'Your full report is ready',
    auditGateText: 'Enter your email to unlock the complete breakdown, your top 5 fixes, and a personalized action plan.',
    auditGatePlaceholder: 'you@company.com',
    auditGateBtn: 'Unlock My Report',
    auditGateNote: 'No spam. We may follow up once about your results.',
    auditGateError: 'Please enter a valid email.',

    // Auth page
    authTagline: 'Client Access',
    authWelcomeTitle: 'Welcome to CEXUS',
    authWelcomeText: 'Sign in to your account or create one in seconds.',
    authNameLabel: 'Full Name',
    authEmailLabel: 'Email',
    authPasswordLabel: 'Password',
    authPasswordHint: 'At least 8 characters',
    authSignInBtn: 'Sign In',
    authSignUpBtn: 'Create Account',
    authNoAccount: "Don't have an account?",
    authHaveAccount: 'Already have an account?',
    authSwitchSignUp: 'Sign up',
    authSwitchSignIn: 'Sign in',
    authGenericError: 'Something went wrong. Please try again.',
    authVerifyBanner: 'Please confirm your email address. We sent a confirmation link to your inbox.',
    verifyTitle: 'Email Confirmed',
    verifyText: 'Your account is fully activated. Welcome to CEXUS.',
    verifyFailTitle: 'Link Invalid or Expired',
    verifyFailText: 'This confirmation link is not valid or was already used.',
    verifyGoDashboard: 'Go to My Dashboard',
    czAuthTitle: 'Create an account to complete your order',
    czAuthText: 'Your project will be linked to your account so you can track it and manage your services from your dashboard.',
  },
  fr: {
    // Nav
    home: 'Accueil',
    about: '\u00C0 Propos',
    services: 'Services',
    ecosystem: '\u00C9cosyst\u00E8me',
    contact: 'Contact',

    // Hero
    heroTagline: 'Conception web, publicit\u00E9s et croissance num\u00E9rique sous un m\u00EAme toit',
    heroTitle: 'Nous Cr\u00E9ons des Sites Web, G\u00E9rons vos Pubs et D\u00E9veloppons votre Business',
    heroSubtitle: 'CEXUS est l\'\u00E9quipe derri\u00E8re les sites web performants, les campagnes publicitaires cibl\u00E9es et les \u00E9cosyst\u00E8mes num\u00E9riques complets. Nous g\u00E9rons tout, de la conception au lancement \u00E0 la croissance.',
    heroCtaPrimary: 'Commencer',
    heroCtaSecondary: 'Explorer l\'\u00C9cosyst\u00E8me',

    // Stats
    stat1Number: '6+',
    stat1Label: 'Plateformes Construites et Aliment\u00E9es',
    stat2Number: '100%',
    stat2Label: 'Support D\u00E9di\u00E9 et Personnalis\u00E9',

    // Why Us
    whyUsTag: 'Pourquoi CEXUS',
    whyUsTitle: 'Parce que Votre Entreprise M\u00E9rite Plus qu\'un Template',
    whyUsSubtitle: 'Nous sommes pass\u00E9s par l\u00E0 aussi. Se battre pour faire voir sa marque, g\u00E9rer la tech, essayer de grandir sans s\'\u00E9puiser. C\'est exactement pourquoi CEXUS existe.',
    whyUs1Title: 'On ne Construit pas que des Sites. On Construit des Business',
    whyUs1Desc: 'N\'importe qui peut mettre une page en ligne. Nous concevons des sites web qui attirent vraiment des clients, b\u00E2tissent la confiance et grandissent avec vous.',
    whyUs2Title: 'Des Publicit\u00E9s qui Convertissent Vraiment',
    whyUs2Desc: 'Nous ne faisons pas que lancer des pubs. Nous cr\u00E9ons des campagnes cibl\u00E9es sur Google, Meta et les r\u00E9seaux sociaux qui am\u00E8nent de vrais clients.',
    whyUs3Title: 'Construit par des Gens qui Comprennent Vraiment',
    whyUs3Desc: 'Nous ne sommes pas une agence sans visage. Nous sommes une \u00E9quipe restreinte et efficace qui sait ce qu\'il faut pour grandir \u00E0 partir de z\u00E9ro.',
    whyUs4Title: '\u00C9voluez Sans Limites',
    whyUs4Desc: 'Que vous lanciez votre premier site ou que vous vous \u00E9tendiez \u00E0 de nouveaux march\u00E9s, l\'infrastructure CEXUS est con\u00E7ue pour grandir avec vous.',

    // About
    aboutTag: '\u00C0 Propos de la Plateforme CEXUS',
    aboutTitle: 'Un R\u00E9seau Num\u00E9rique Unifi\u00E9 et R\u00E9silient',
    aboutVision: 'L\'\u00E9cosyst\u00E8me CEXUS envisage un monde o\u00F9 les individus et communaut\u00E9s \u00E9voluent gr\u00E2ce \u00E0 la contribution significative et l\'infrastructure partag\u00E9e. CEXUS est le r\u00E9seau num\u00E9rique de confiance connectant les id\u00E9es \u00E0 l\'ex\u00E9cution.',
    whoWeAreTag: 'Qu\'est-ce que CEXUS',
    whoWeAreTitle: 'Infrastructure Ind\u00E9pendante, Potentiel Illimit\u00E9',
    whoWeAreText: 'CEXUS est une plateforme num\u00E9rique s\u00E9curis\u00E9e et un syst\u00E8me d\'infrastructure ind\u00E9pendant con\u00E7u pour alimenter les plateformes et communaut\u00E9s \u00E0 grande \u00E9chelle.',
    missionTag: 'La Mission CEXUS',
    missionTitle: 'Permettre une Collaboration Num\u00E9rique S\u00FBre et \u00C9volutive',
    missionText: 'Notre mission est de supprimer les barri\u00E8res qui limitent la croissance des entreprises et communaut\u00E9s en ligne. La plateforme CEXUS fournit les cadres et l\'infrastructure permettant aux \u00E9cosyst\u00E8mes de fonctionner de mani\u00E8re ind\u00E9pendante.',

    // Services
    servicesTag: 'Ce Que Nous Faisons',
    servicesTitle: 'Conception Web, Publicit\u00E9 et Croissance Num\u00E9rique',
    servicesSubtitle: 'Des sites web sur mesure aux campagnes publicitaires et \u00E9cosyst\u00E8mes complets, on s\'occupe de tout.',
    service1Title: 'Conception et D\u00E9veloppement Web',
    service1Desc: 'Des sites sur mesure qui sont beaux, rapides et qui convertissent vos visiteurs en clients.',
    service2Title: 'Publicit\u00E9s Google et R\u00E9seaux Sociaux',
    service2Desc: 'Des campagnes publicitaires cibl\u00E9es sur Google, Facebook, Instagram et plus. Nous cr\u00E9ons et optimisons vos pubs pour des r\u00E9sultats mesurables.',
    service3Title: 'Infrastructure et Croissance d\'Entreprise',
    service3Desc: 'Les syst\u00E8mes et la strat\u00E9gie en coulisses qui font tourner votre entreprise sans accroc.',

    // Projects
    projectsTag: 'Notre \u00C9cosyst\u00E8me',
    projectsTitle: 'Plateformes Propuls\u00E9es par CEXUS',
    projectsSubtitle: 'Chaque plateforme fonctionne de mani\u00E8re ind\u00E9pendante tout en tirant parti de l\'infrastructure CEXUS.',

    bituPros: 'BituPros Solutions',
    bituProsDesc: 'Une plateforme de solutions compl\u00E8te aidant les entreprises \u00E0 \u00E9tablir et renforcer leur pr\u00E9sence num\u00E9rique avec des services web professionnels propuls\u00E9s par CEXUS.',
    streetzIndeed: 'Streetz Indeed (SI)',
    streetzDesc: 'Une plateforme num\u00E9rique de confiance con\u00E7ue pour amplifier les voix authentiques et cr\u00E9er des opportunit\u00E9s r\u00E9elles gr\u00E2ce au contenu et \u00E0 la communaut\u00E9.',
    designerMarket: 'Designer Market',
    designerMarketDesc: 'Un march\u00E9 cr\u00E9atif connectant designers et clients, offrant des services de design premium et des solutions personnalis\u00E9es.',
    alturaLiving: 'Altura Living',
    alturaLivingDesc: 'Une plateforme lifestyle premium \u00E9levant la fa\u00E7on dont les gens d\u00E9couvrent et vivent des espaces, services et solutions de vie modernes.',
    ovigo: 'Ovigo',
    ovigoDesc: 'Une plateforme sociale de covoiturage connectant conducteurs et passagers pour des trajets partag\u00E9s \u00E0 travers les communaut\u00E9s. Rendre les d\u00E9placements plus abordables, sociaux et durables.',
    enterPlatform: 'Visiter la Plateforme',
    explorePlatform: 'Visiter la Plateforme',

    comingSoonTag: 'Bient\u00F4t Disponible',
    comingSoonTitle: 'Prochaines Plateformes',
    mtlNetwork: 'MTL Music Network',
    mtlDesc: 'Une plateforme compl\u00E8te de l\'industrie musicale connectant artistes, producteurs et professionnels \u00E0 travers l\'\u00E9cosyst\u00E8me montr\u00E9alais. En d\u00E9veloppement.',
    comingSoonLabel: 'Bient\u00F4t',

    // Contact
    contactTag: 'Contactez-Nous',
    contactTitle: 'Construisons Quelque Chose Ensemble',
    contactSubtitle: 'Pr\u00EAt \u00E0 propulser votre entreprise? Remplissez le formulaire et nous vous recontacterons.',
    contactNameLabel: 'Nom Complet',
    contactEmailLabel: 'Adresse Email',
    contactPhoneLabel: 'Num\u00E9ro de T\u00E9l\u00E9phone',
    contactBusinessLabel: 'Nom de l\'Entreprise',
    contactWebsiteLabel: 'Votre Site Web (si existant)',
    contactLinkedinLabel: 'Profil Google ou URL LinkedIn',
    contactMessageLabel: 'Parlez-nous de votre projet',
    contactSubmit: 'Envoyer le Message',
    contactInfoTitle: 'Contactez-Nous Directement',
    contactInfoText: 'Vous pr\u00E9f\u00E9rez nous contacter directement? \u00C9crivez-nous. Nous sommes de vraies personnes et nous r\u00E9pondons vite.',

    // Footer
    footerTagline: 'Conception web, publicit\u00E9 et infrastructure num\u00E9rique pour les entreprises en croissance. La plateforme CEXUS alimente des r\u00E9seaux communautaires de confiance.',
    footerRights: 'Tous droits r\u00E9serv\u00E9s.',
    footerFoundedBy: 'Fond\u00E9 par',
    footerEcosystem: '\u00C9cosyst\u00E8me',
    contact: 'Contact',
    beyondReality: 'Au-del\u00E0 de la R\u00E9alit\u00E9',

    // Website Services page
    viewPricing: 'Voir Services et Tarifs',
    wsTag: 'Services Web',
    wsTitle: 'Des Sites Web Qui Font Cro\u00EEtre Votre Entreprise.',
    wsSubtitle: 'Nous concevons des sites web rapides, modernes et ax\u00E9s sur la conversion qui aident les entreprises \u00E0 attirer plus de clients, b\u00E2tir la confiance et augmenter leurs revenus.',
    wsCtaPrimary: 'Cr\u00E9er Mon Site Web',
    wsCtaSecondary: 'Voir les Forfaits',
    wsPricingTag: 'Tarification',
    wsPricingTitle: 'Choisissez Votre Forfait de Croissance',
    wsPricingSubtitle: 'Prix transparents. Qualit\u00E9 premium. Chaque forfait est con\u00E7u pour convertir vos visiteurs en clients.',
    wsMostPopular: 'Le Plus Populaire',
    wsStartingAt: '\u00C0 partir de',
    wsBuilderTag: 'Configurateur Interactif',
    wsBuilderTitle: 'Personnalisez Votre Site Web',
    wsBuilderSubtitle: 'S\u00E9lectionnez un forfait, ajoutez les fonctionnalit\u00E9s dont votre entreprise a besoin et voyez votre investissement se mettre \u00E0 jour en temps r\u00E9el.',
    wsStepPackage: '1. S\u00E9lectionnez votre forfait',
    wsStepAddons: '2. Choisissez vos options',
    wsYourBuild: 'Votre Configuration',
    wsBasePackage: 'Forfait de base',
    wsNoAddons: 'Aucune option s\u00E9lectionn\u00E9e',
    wsOneTimeTotal: 'Total unique',
    wsMonthlyServices: 'Services mensuels',
    wsPerMonth: '/mois',
    wsSendQuote: 'Envoyer Ma Soumission',
    wsOrContactPage: 'Ou continuez sur la page contact',
    wsQuoteFormTitle: 'O\u00F9 devons-nous envoyer votre soumission?',
    wsQuoteName: 'Nom Complet',
    wsQuoteEmail: 'Adresse Email',
    wsQuotePhone: 'Num\u00E9ro de T\u00E9l\u00E9phone',
    wsQuoteSubmit: 'Envoyer la Demande',
    wsQuoteSending: 'Envoi...',
    wsQuoteSuccess: 'Demande envoy\u00E9e! Nous vous r\u00E9pondrons dans les 24 heures.',
    wsQuoteError: 'Une erreur est survenue. Veuillez r\u00E9essayer ou utiliser la page contact.',
    wsCompareTag: 'Comparer',
    wsCompareTitle: 'Chaque Fonctionnalit\u00E9, C\u00F4te \u00E0 C\u00F4te',
    wsCompareSubtitle: 'Voyez exactement ce qui est inclus dans chaque forfait.',
    wsCompareFeature: 'Fonctionnalit\u00E9',
    wsFaqTag: 'FAQ',
    wsFaqTitle: 'Questions Fr\u00E9quentes',
    wsFinalTitle: 'Construisons Quelque Chose d\'Exceptionnel.',
    wsFinalText: 'Que vous lanciez une nouvelle entreprise ou modernisiez votre pr\u00E9sence en ligne, Cexus livre des sites web con\u00E7us pour convertir vos visiteurs en clients.',
    wsFinalCta: 'D\u00E9marrer Mon Projet',

    // Game menu / cinematic services
    gmMenuPackages: 'Voir les Forfaits',
    gmMenuCustomize: 'Personnaliser Votre Site',
    gmMenuCompare: 'Comparer les Fonctionnalit\u00E9s',
    gmMenuStart: 'D\u00E9marrer Mon Projet',
    gmSelectHint: 'S\u00E9lectionnez une option pour commencer',
    carouselTag: 'Choisissez Votre Forfait',
    carouselTitle: 'Quatre Chemins. Une Destination : La Croissance.',
    carouselSelect: 'S\u00E9lectionner et Personnaliser',
    carouselContact: 'Demander via contact',
    techTag: 'Notre Arsenal',
    techTitle: 'Technologies et Plateformes avec Lesquelles Nous Travaillons',
    czTeaserTag: 'Exp\u00E9rience Interactive',
    czTeaserText: 'Entrez dans notre studio interactif. Choisissez votre forfait, boostez-le avec des options, ajustez votre budget publicitaire et r\u00E9servez votre projet, le tout dans une exp\u00E9rience immersive.',
    czStart: 'D\u00C9MARRER',
    czSkip: 'Passer l\'Intro',
    czIntroLine1: 'INITIALISATION DE LA S\u00C9QUENCE',
    czIntroLine2: 'Pr\u00E9paration de votre exp\u00E9rience...',
    czChoosePackage: 'S\u00E9lectionnez Votre Forfait',
    czChooseSub: 'Chaque projet commence par une fondation. Choisissez la v\u00F4tre.',
    czBuildTitle: 'Boostez Votre Projet',
    czAddonsLabel: 'Options',
    czAdsTitle: 'Gestion Publicitaire',
    czAdsSub: 'Laissez-nous g\u00E9rer vos campagnes. Ajustez votre budget mensuel. Nous sugg\u00E9rons un point de d\u00E9part selon votre forfait.',
    czSuggested: 'Sugg\u00E9r\u00E9 pour',
    czMonthlyBudget: 'Budget mensuel',
    czDeposit: 'D\u00E9p\u00F4t de 35% d\u00FB aujourd\'hui',
    czRemaining: 'Solde \u00E0 la livraison',
    czSecurePay: 'Payer le D\u00E9p\u00F4t et R\u00E9server Mon Projet',
    czYourInfo: 'Vos coordonn\u00E9es pour compl\u00E9ter le paiement',
    czProceedPay: 'Proc\u00E9der au Paiement S\u00E9curis\u00E9',
    czRedirecting: 'Redirection vers le paiement s\u00E9curis\u00E9...',
    czChangePackage: 'Changer de forfait',
    czPayNote: 'S\u00E9curis\u00E9 par Stripe. Les services mensuels se renouvellent automatiquement et peuvent \u00EAtre annul\u00E9s en tout temps depuis votre tableau de bord. Le solde n\'est d\u00FB qu\'\u00E0 la livraison de votre projet.',
    czPayError: 'Impossible de d\u00E9marrer le paiement. R\u00E9essayez ou contactez-nous.',
    paySuccessTitle: 'D\u00E9p\u00F4t Re\u00E7u. Construisons.',
    paySuccessText: 'Votre projet est officiellement r\u00E9serv\u00E9. Un courriel de confirmation avec le r\u00E9sum\u00E9 complet est en route. Notre \u00E9quipe vous contactera dans les 24 heures pour lancer le projet.',
    payChecking: 'V\u00E9rification de votre paiement...',
    payFailedTitle: 'Paiement Non Compl\u00E9t\u00E9',
    payFailedText: 'Votre session de paiement a expir\u00E9 ou a \u00E9t\u00E9 annul\u00E9e. Aucun montant n\'a \u00E9t\u00E9 pr\u00E9lev\u00E9.',
    payBackServices: 'Retour aux Services',
    payTryAgain: 'R\u00E9essayer',
    faqPageTag: 'Support',
    footerLegal: 'L\u00E9gal',
    footerFaq: 'FAQ',
    footerPrivacy: 'Politique de Confidentialit\u00E9',
    footerTerms: 'Conditions G\u00E9n\u00E9rales',

    // AI Website Audit
    gmMenuAudit: 'Bilan de Sant\u00E9 Web Gratuit',
    auditTag: 'Bilan Web Gratuit',
    auditTitle: 'Votre Site Web Est-Il En Sant\u00E9?',
    auditSubtitle: 'D\u00E9posez votre lien et obtenez une \u00E9valuation professionnelle de votre site en quelques secondes. Une note sur 100 et exactement ce qui vous co\u00FBte des clients. Gratuit, sans engagement.',
    auditPlaceholder: 'votresite.com',
    auditButton: 'Lancer l\'Audit Gratuit',
    auditStep1: 'Exploration de vos pages...',
    auditStep2: 'Analyse du design et de l\'exp\u00E9rience...',
    auditStep3: 'V\u00E9rification des signaux SEO...',
    auditStep4: '\u00C9valuation du potentiel de conversion...',
    auditWait: 'Cela prend environ 30 secondes',
    auditErrorReach: 'Impossible d\'acc\u00E9der \u00E0 ce site. V\u00E9rifiez l\'adresse et r\u00E9essayez.',
    auditErrorGeneric: 'L\'analyse a \u00E9chou\u00E9. Veuillez r\u00E9essayer dans un instant.',
    auditScoreLabel: 'Note Globale',
    auditPagesScanned: 'pages analys\u00E9es',
    auditBreakdown: 'Le D\u00E9tail',
    auditTopFixes: 'Top 5 des Correctifs',
    auditRecoTitle: 'Nos Recommandations',
    auditRecoSub: 'Selon nos constats, ces services Cexus auraient le plus grand impact.',
    auditRecoPackage: 'Forfait id\u00E9al',
    auditCtaBuild: 'Corriger avec Cexus',
    auditCtaTalk: 'Parlez-nous d\'abord',
    auditAnother: 'Auditer un autre site',

    // Contact wizard
    cwStep1Title: '\u00C0 Propos de Vous',
    cwStep2Title: 'Votre Entreprise',
    cwStep3Title: 'Votre Projet',
    cwNext: 'Suivant',
    cwBack: 'Retour',
    contactGoogleLabel: 'Page Google Business',
    cwSendInquiry: 'Envoyer la Demande',
    cwSending: 'Envoi...',
    cwOptionalHint: 'Optionnel, mais utile pour bien pr\u00E9parer votre projet.',
    cwSuccessTitle: 'Demande Envoy\u00E9e',
    cwSuccessText: 'Merci de nous avoir contact\u00E9s. Nous vous r\u00E9pondrons dans les 24 heures.',
    cwSendAnother: 'Envoyer une autre demande',
    cwFillRequired: 'Veuillez remplir tous les champs requis.',
    cwInvalidEmail: 'Veuillez entrer une adresse email valide.',
    contactPhoneTitle: 'T\u00E9l\u00E9phone',

    // Auth + dashboard
    navSignIn: 'Connexion',
    navDashboard: 'Tableau de Bord',
    navSignOut: 'D\u00E9connexion',
    dbTag: 'Espace Client',
    dbWelcome: 'Bon retour',
    dbSignInTitle: 'Votre Espace Client',
    dbSignInText: 'Connectez-vous ou cr\u00E9ez un compte pour voir vos achats, g\u00E9rer vos abonnements et suivre vos projets.',
    dbSignInBtn: 'Se connecter ou cr\u00E9er un compte',
    dbOrdersTitle: 'Vos Achats',
    dbNoOrders: 'Aucun achat pour le moment. Vos projets et services appara\u00EEtront ici.',
    dbBrowse: 'Explorer les Services',
    dbSubsTitle: 'Vos Abonnements',
    dbNoSubs: 'Aucun abonnement actif.',
    dbCancelSub: 'Annuler l\'Abonnement',
    dbCancelConfirm: 'Annuler \u00E0 la fin de la p\u00E9riode de facturation en cours?',
    dbCancelKeep: 'Le garder',
    dbCancelYes: 'Oui, annuler',
    dbCancelScheduled: 'Annulation en fin de p\u00E9riode',
    dbActive: 'Actif',
    dbRenews: 'Renouvellement',
    dbDeposit: 'D\u00E9p\u00F4t pay\u00E9',
    dbBalance: 'Solde \u00E0 la livraison',
    dbMonthlyLabel: 'Mensuel',
    dbOrderDate: 'Achet\u00E9 le',
    dbStatusPaid: 'Pay\u00E9',

    // Admin
    adTag: 'Contr\u00F4le Admin',
    adTitle: 'Vue d\'Ensemble',
    adRevenue: 'D\u00E9p\u00F4ts Encaiss\u00E9s',
    adMrr: 'R\u00E9current Mensuel',
    adUsers: 'Utilisateurs',
    adOrders: 'Commandes Pay\u00E9es',
    adSubs: 'Abonnements Actifs',
    adLeads: 'Prospects Audit',
    adActivity: 'Activit\u00E9 R\u00E9cente',
    adCustomers: 'Clients',
    adSearchPh: 'Rechercher nom ou email...',
    adAllServices: 'Tous les services',
    adMinSpent: 'D\u00E9pense min (CA$)',
    adColOrders: 'Commandes',
    adColSpent: 'Total d\u00E9pens\u00E9',
    adNoCustomers: 'Aucun client ne correspond \u00E0 ces filtres.',
    adBack: 'Retour aux clients',
    adRegistered: 'Compte Google',
    adGuest: 'Achat invit\u00E9',
    adPurchases: 'Historique d\'Achats',
    adLeadHistory: 'Prospects Audit',
    adNoOrders: 'Aucun achat enregistr\u00E9.',

    // Audit email gate
    auditGateTitle: 'Votre rapport complet est pr\u00EAt',
    auditGateText: 'Entrez votre email pour d\u00E9bloquer l\'analyse compl\u00E8te, vos 5 correctifs prioritaires et un plan d\'action personnalis\u00E9.',
    auditGatePlaceholder: 'vous@entreprise.com',
    auditGateBtn: 'D\u00E9bloquer Mon Rapport',
    auditGateNote: 'Pas de spam. Nous pourrions faire un suivi unique sur vos r\u00E9sultats.',
    auditGateError: 'Veuillez entrer un email valide.',

    // Auth page
    authTagline: 'Acc\u00E8s Client',
    authWelcomeTitle: 'Bienvenue chez CEXUS',
    authWelcomeText: 'Connectez-vous \u00E0 votre compte ou cr\u00E9ez-en un en quelques secondes.',
    authNameLabel: 'Nom Complet',
    authEmailLabel: 'Email',
    authPasswordLabel: 'Mot de passe',
    authPasswordHint: 'Au moins 8 caract\u00E8res',
    authSignInBtn: 'Se Connecter',
    authSignUpBtn: 'Cr\u00E9er un Compte',
    authNoAccount: 'Pas encore de compte?',
    authHaveAccount: 'D\u00E9j\u00E0 un compte?',
    authSwitchSignUp: 'Inscrivez-vous',
    authSwitchSignIn: 'Connectez-vous',
    authGenericError: 'Une erreur est survenue. Veuillez r\u00E9essayer.',
    authVerifyBanner: 'Veuillez confirmer votre adresse email. Un lien de confirmation vous a \u00E9t\u00E9 envoy\u00E9.',
    verifyTitle: 'Email Confirm\u00E9',
    verifyText: 'Votre compte est enti\u00E8rement activ\u00E9. Bienvenue chez CEXUS.',
    verifyFailTitle: 'Lien Invalide ou Expir\u00E9',
    verifyFailText: 'Ce lien de confirmation n\'est pas valide ou a d\u00E9j\u00E0 \u00E9t\u00E9 utilis\u00E9.',
    verifyGoDashboard: 'Aller \u00E0 Mon Tableau de Bord',
    czAuthTitle: 'Cr\u00E9ez un compte pour finaliser votre commande',
    czAuthText: 'Votre projet sera li\u00E9 \u00E0 votre compte pour le suivre et g\u00E9rer vos services depuis votre tableau de bord.',
  },
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('cexus-language');
    return saved || 'en';
  });

  useEffect(() => {
    localStorage.setItem('cexus-language', language);
  }, [language]);

  const t = (key) => translations[language][key] || key;

  const toggleLanguage = () => {
    setLanguage((prev) => (prev === 'en' ? 'fr' : 'en'));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
